import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  useMediaQuery,
  Typography,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogin } from "state";
import Dropzone from "react-dropzone";
import { BASE_URL } from "../../config";
import uploadToCloudinary from "../../uploadToCloudinary";

const registerSchema = yup.object().shape({
  firstName: yup.string().required("required"),
  lastName: yup.string().required("required"),
  email: yup.string().email("invalid email").required("required"),
  password: yup.string().required("required"),
  location: yup.string().required("required"),
  occupation: yup.string().required("required"),
  picture: yup.string().required("required"),
});

const loginSchema = yup.object().shape({
  email: yup.string().email("invalid email").required("required"),
  password: yup.string().required("required"),
});

const initialValuesRegister = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  location: "",
  occupation: "",
  picture: "",
};

const initialValuesLogin = {
  email: "",
  password: "",
};

const Form = () => {
  const [pageType, setPageType] = useState("login");
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const isLogin = pageType === "login";
  const isRegister = pageType === "register";

  const register = async (values, onSubmitProps) => {
    try {
      setIsUploading(true);
      setErrorMessage("");

      // Upload profile picture to Cloudinary
      let picturePath = "";
      if (values.picture) {
        picturePath = await uploadToCloudinary(values.picture);
      }

      // Send registration data with Cloudinary URL
      const savedUserResponse = await fetch(
        `${BASE_URL}/auth/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            location: values.location,
            occupation: values.occupation,
            picturePath: picturePath,
          }),
        }
      );

      const savedUserData = await savedUserResponse.json();

      // Check if registration was successful
      if (!savedUserResponse.ok) {
        setIsUploading(false);
        setErrorMessage(savedUserData.error || savedUserData.msg || "Registration failed. Please try again.");
        return;
      }

      // Automatically log in the user after successful registration
      const loggedInResponse = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
      });
      const loggedInData = await loggedInResponse.json();

      setIsUploading(false);

      // Check if login was successful
      if (!loggedInResponse.ok) {
        setErrorMessage(loggedInData.error || loggedInData.msg || "Login failed. Please try again.");
        return;
      }

      // Successfully registered and logged in
      if (loggedInData.token && loggedInData.user) {
        dispatch(
          setLogin({
            user: loggedInData.user,
            token: loggedInData.token,
          })
        );
        onSubmitProps.resetForm();
        navigate("/");
      }
    } catch (error) {
      setIsUploading(false);
      setErrorMessage(error.message || "Failed to register. Please try again.");
      console.error("Registration error:", error);
    }
  };

  const login = async (values, onSubmitProps) => {
    try {
      setErrorMessage("");

      const loggedInResponse = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const loggedInData = await loggedInResponse.json();

      // Check if login was successful
      if (!loggedInResponse.ok) {
        setErrorMessage(loggedInData.error || loggedInData.msg || "Login failed. Please check your credentials.");
        return;
      }

      // Successfully logged in
      if (loggedInData.token && loggedInData.user) {
        onSubmitProps.resetForm();
        dispatch(
          setLogin({
            user: loggedInData.user,
            token: loggedInData.token,
          })
        );
        navigate("/");
      }
    } catch (error) {
      setErrorMessage(error.message || "An error occurred during login. Please try again.");
      console.error("Login error:", error);
    }
  };

  const handleFormSubmit = async (values, onSubmitProps) => {
    if (isLogin) await login(values, onSubmitProps);
    if (isRegister) await register(values, onSubmitProps);
  };

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={isLogin ? initialValuesLogin : initialValuesRegister}
      validationSchema={isLogin ? loginSchema : registerSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        resetForm,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box
            display="grid"
            gap="15px"
            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            sx={{
              "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
            }}
          >
            {isRegister && (
              <>
                <Box
                  gridColumn="span 4"
                  display="flex"
                  justifyContent="center"
                  mb="1rem"
                >
                  <Dropzone
                    acceptedFiles=".jpg,.jpeg,.png"
                    multiple={false}
                    onDrop={(acceptedFiles) =>
                      setFieldValue("picture", acceptedFiles[0])
                    }
                  >
                    {({ getRootProps, getInputProps }) => (
                      <Box
                        {...getRootProps()}
                        width="100px"
                        height="100px"
                        borderRadius="50%"
                        border={`2px dashed ${palette.primary.main}`}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        overflow="hidden"
                        sx={{
                          "&:hover": { cursor: "pointer", backgroundColor: palette.primary.light },
                          backgroundColor: palette.neutral.light,
                          position: "relative"
                        }}
                      >
                        <input {...getInputProps()} />
                        {!values.picture ? (
                          <Box textAlign="center" p="0.5rem">
                            <EditOutlinedIcon sx={{ fontSize: "2rem", color: palette.primary.main }} />
                            <Typography variant="caption" display="block">Add Photo</Typography>
                          </Box>
                        ) : (
                          <img
                            src={URL.createObjectURL(values.picture)}
                            alt="profile"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </Box>
                    )}
                  </Dropzone>
                </Box>
                <TextField
                  label="First Name"
                  size="small"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.firstName}
                  name="firstName"
                  error={
                    Boolean(touched.firstName) && Boolean(errors.firstName)
                  }
                  helperText={touched.firstName && errors.firstName}
                  sx={{ gridColumn: "span 2", "& .MuiOutlinedInput-root": { borderRadius: "25px" } }}
                />
                <TextField
                  label="Last Name"
                  size="small"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.lastName}
                  name="lastName"
                  error={Boolean(touched.lastName) && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  sx={{ gridColumn: "span 2", "& .MuiOutlinedInput-root": { borderRadius: "25px" } }}
                />
                <TextField
                  label="Location"
                  size="small"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.location}
                  name="location"
                  error={Boolean(touched.location) && Boolean(errors.location)}
                  helperText={touched.location && errors.location}
                  sx={{ gridColumn: "span 4", "& .MuiOutlinedInput-root": { borderRadius: "25px" } }}
                />
                <TextField
                  label="Occupation"
                  size="small"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.occupation}
                  name="occupation"
                  error={
                    Boolean(touched.occupation) && Boolean(errors.occupation)
                  }
                  helperText={touched.occupation && errors.occupation}
                  sx={{ gridColumn: "span 4", "& .MuiOutlinedInput-root": { borderRadius: "25px" } }}
                />

              </>
            )}

            <TextField
              label="Email"
              size="small"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.email}
              name="email"
              error={Boolean(touched.email) && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              sx={{ gridColumn: "span 4", "& .MuiOutlinedInput-root": { borderRadius: "25px" } }}
            />
            <TextField
              label="Password"
              size="small"
              type="password"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.password}
              name="password"
              error={Boolean(touched.password) && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              sx={{ gridColumn: "span 4", "& .MuiOutlinedInput-root": { borderRadius: "25px" } }}
            />
          </Box>

          {/* BUTTONS */}
          {/* BUTTONS */}
          <Box display="flex" flexDirection="column" alignItems="center">
            {errorMessage && (
              <Typography
                color="error"
                sx={{
                  mt: "1rem",
                  p: "0.75rem",
                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                  borderRadius: "5px",
                  fontSize: "0.875rem",
                  width: "100%",
                  textAlign: "center"
                }}
              >
                {errorMessage}
              </Typography>
            )}
            <Button
              type="submit"
              disabled={isUploading}
              sx={{
                m: "1.5rem 0 1.5rem 0",
                p: "0.5rem 2rem",
                borderRadius: "3rem",
                backgroundColor: palette.primary.main,
                color: palette.background.alt,
                fontSize: "12px",
                fontWeight: "bold",
                "&:hover": {
                  color: palette.background.alt,
                  backgroundColor: palette.primary.dark,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)"
                },
                "&:disabled": {
                  backgroundColor: palette.neutral.light,
                  color: palette.neutral.main,
                },
              }}
            >
              {isUploading ? "UPLOADING..." : isLogin ? "LOGIN" : "REGISTER"}
            </Button>
            <Typography
              onClick={() => {
                setPageType(isLogin ? "register" : "login");
                resetForm();
              }}
              sx={{
                textDecoration: "underline",
                color: palette.primary.main,
                textAlign: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: palette.primary.dark,
                },
              }}
            >
              {isLogin
                ? "Don't have an account? Sign Up here."
                : "Already have an account? Login here."}
            </Typography>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default Form;